import { Box, Fab, List } from '@mui/material'
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ItineraryLeg from 'src/components/plan/PlanItineraryLeg'
import PlanDisplay from 'src/components/plan/PlanDisplay'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import SheetHeader from 'src/components/sheet/SheetHeader'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { useGetVehicleLocationsTripsTripIdVehiclesGet } from 'src/generated/brezavta/queries'
import { Plan, VehicleLocation } from 'src/generated/brezavta/requests/types.gen'
import { getColorForMode } from 'src/lib/modes'
import { calculateBoundsForLegs, useLayerData, registerVehicleImages, colorToImageId, normalizeVehicleColor, darkenColor } from 'src/lib/util'

import NavigationIcon from '@mui/icons-material/Navigation'

export const Route = createFileRoute('/plan/itinerary')({
    component: Plan2Itinerary,
})

const VEHICLE_REFRESH_INTERVAL_MS = 15000

function Plan2Itinerary() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const state = useRouterState()
    const { map, mapLoaded } = useContext(MapRenderContext)

    const routerState = state.location.state as unknown as { plan?: Plan, planSearch?: unknown }
    const plan: Plan | undefined = routerState?.plan
    const planSearch = routerState?.planSearch

    const [tripIds, setTripIds] = useState<string>('')

    const { data: vehicleData, refetch: vehicleRefetch } = useGetVehicleLocationsTripsTripIdVehiclesGet({
        path: { trip_id: tripIds },
    }, undefined, { enabled: tripIds !== '' })

    // Map: route polylines
    const layerData = plan && {
        type: 'FeatureCollection',
        features: plan.legs.map(leg => ({
            type: 'Feature',
            geometry: leg.geometry,
            properties: { color: getColorForMode(leg), casingColor: darkenColor(getColorForMode(leg), 0.45) },
        })),
    }
    useLayerData('planView', layerData, map, mapLoaded)

    // Map: fit bounds to route
    useEffect(() => {
        if (!mapLoaded || !plan) return
        const bounds = calculateBoundsForLegs(plan.legs) as [[number, number], [number, number]]
        map!.current!.fitBounds(bounds, { padding: 50 })
    }, [plan, mapLoaded, map])

    // Extract trip IDs for vehicle tracking
    useEffect(() => {
        if (!plan) return
        const ids = plan.legs.map(leg => leg.trip?.gtfs_id).filter(Boolean)
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTripIds(ids.join(','))
    }, [plan])

    // Map: vehicle locations
    const vehicleLocationsData = vehicleData && {
        type: 'FeatureCollection',
        features: vehicleData.map((vehicleLocation: VehicleLocation) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [vehicleLocation.lon, vehicleLocation.lat] },
            properties: {
                color: normalizeVehicleColor(vehicleLocation.color || ''),
                color_id: colorToImageId(normalizeVehicleColor(vehicleLocation.color || '')),
                heading: vehicleLocation.heading ?? 0,
            },
        })),
    }
    useLayerData('planVehicleLocations', vehicleLocationsData, map, mapLoaded)

    // Register vehicle images when vehicle data changes
    useEffect(() => {
        if (!vehicleData || !map?.current) return
        const uniqueColors = [...new Set(vehicleData.map((v: VehicleLocation) => normalizeVehicleColor(v.color || '')))] as string[]
        registerVehicleImages(map.current, uniqueColors)
    }, [vehicleData, map])

    // Refresh vehicle locations periodically
    useEffect(() => {
        if (!tripIds) return
        const interval = setInterval(() => vehicleRefetch(), VEHICLE_REFRESH_INTERVAL_MS)
        return () => clearInterval(interval)
    }, [tripIds, vehicleRefetch])

    if (!plan) {
        return (
            <AutomaticSheet>
                <SheetHeader heading={t('Trip details')} />
                <Box sx={{ p: 2 }}>{t('No plan data available.')}</Box>
            </AutomaticSheet>
        )
    }

    return (
        <AutomaticSheet>
            <SheetHeader heading={t('Trip details')} />
            <Box sx={{ px: 2, pt: 1, pb: 0 }}>
                <PlanDisplay plan={plan} />
            </Box>
            <List sx={{ width: '100%', overflowX: 'hidden', pb: 10 }}>
                {plan.legs.map((leg, index) => (
                    <div key={index}>
                        <ItineraryLeg
                            leg={leg}
                            nextLeg={plan.legs[index + 1]}
                            previousLeg={plan.legs[index - 1]}
                            isFirst={index === 0}
                            isLast={index === plan.legs.length - 1}
                        />
                    </div>
                ))}
            </List>
            <Fab
                variant="extended"
                color="primary"
                sx={{ position: 'sticky', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}
                onClick={() => navigate({
                    to: '/plan/navigate',
                    state: { plan, planSearch } as Record<string, unknown>,
                })}
            >
                <NavigationIcon sx={{ mr: 1 }} />
                {t('Start')}
            </Fab>
        </AutomaticSheet>
    )
}
